import { useMemo } from 'react';
import styles from './schedule-urls-table.module.scss';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';

import { IconButton } from 'src/components/design-system';
import { Table, cellActionKeyboardNavigate } from 'src/components/design-system/table';

import { colorPalette } from 'shared/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/pro-light-svg-icons';
import { faPencil } from '@fortawesome/pro-solid-svg-icons';

import _ from 'lodash';
import { MODES } from '../workspace-general-info';
import { Popconfirm } from 'antd';
import { LabelsList } from 'src/components/labels';
import { useAccessiblePopconfirm } from 'src/components/design-system/popconfirm/hooks/use-accessible-popconfirm';

const DeleteAction = ({ rowIndex, onDelete, lang }) => {
  const popconfirmProps = useAccessiblePopconfirm({
    onConfirm: () => onDelete(rowIndex),
  });

  return (
    <Popconfirm
      title={lang.DELETE_SCHEDULE_URL}
      cancelText={lang.CANCEL}
      okText={lang.DELETE}
      okType="danger"
      okButtonProps={{ type: 'primary' }}
      cancelButtonProps={{ type: 'default' }}
      {...popconfirmProps}>
      <IconButton label={lang.DELETE}>
        <FontAwesomeIcon icon={faTrashCan} color={colorPalette.red} />
      </IconButton>
    </Popconfirm>
  );
};

/**
 * Schedule Url table component
 * @param {object} props
 * @param {import('../../types').ScheduleUrl[]} props.scheduleUrls the schedule urls list
 * @param {(index: number, mode: 'create' | 'edit') => void} props.openScheduleUrlEditor function to open the schedule url editor
 * @param {(index: number) => Promise<void>} props.onDeleteScheduleUrl function to delete the schedule url
 */
export function ScheduleUrlTable({ scheduleUrls, openScheduleUrlEditor, onDeleteScheduleUrl }) {
  const lang = useSelector(getLang('SETTINGS'));
  const LANG_PROFILE = useSelector(getLang('PROFILE_OPTIONS'));

  const columns = useMemo(
    () => [
      {
        headerName: lang.QGENDA_TITLE_LABEL,
        field: 'title',
        flex: 1,
        cellRenderer: params => (
          <p>{params.value || _.take(params.data.url.replace(/^https?:\/\//, ''), 20)}</p>
        ),
      },
      {
        headerName: lang.PROFESSIONS,
        field: 'professions',
        flex: 1,
        valueFormatter: params => params.value?.map(val => LANG_PROFILE[val]).join(', ') || '',
      },
      {
        headerName: lang.ROLES,
        field: 'roles',
        flex: 1,
        valueFormatter: params => params.value?.map(val => LANG_PROFILE[val]).join(', ') || '',
      },
      {
        headerName: lang.SITES,
        field: 'labels',
        flex: 1,
        cellRenderer: params => <LabelsList labels={params.value} />,
      },
      {
        headerName: lang.ACTIONS,
        field: 'actions',
        width: 120,
        sortable: false,
        suppressKeyboardEvent: cellActionKeyboardNavigate,
        cellRenderer: params => (
          <div className={styles.actions}>
            <IconButton
              label={lang.EDIT}
              onClick={() => openScheduleUrlEditor(params.node.sourceRowIndex, MODES.EDIT)}>
              <FontAwesomeIcon icon={faPencil} color={colorPalette.primary} />
            </IconButton>
            <DeleteAction
              rowIndex={params.node.sourceRowIndex}
              onDelete={onDeleteScheduleUrl}
              lang={lang}
            />
          </div>
        ),
      },
    ],
    [lang, LANG_PROFILE, openScheduleUrlEditor, onDeleteScheduleUrl],
  );

  if (!scheduleUrls?.length) {
    return null;
  }

  return (
    <Table
      containerProps={{ className: styles.table }}
      columnDefs={columns}
      rowData={scheduleUrls}
      getRowId={params => params.data.url || `row-${params.rowIndex}`}
      domLayout="autoHeight"
      pagination={false}
    />
  );
}
